import { BaseService } from "./BaseService";
import { IPartnerDashboardService } from "./interfaces/IPartnerDashboardService";
import {
  PartnerDateRangeQuery,
  PartnerHotelExportQuery,
  PartnerHotelFilterOption,
  PartnerHotelOverview,
  PartnerHotelOverviewQuery,
  PartnerHotelRevenueRank,
  PartnerRevenueLogsExportQuery,
  PartnerRevenueLogsQuery,
  PartnerRevenueLogsResponse,
  PartnerTotalRevenueSummary,
} from "@/types/partner-dashboard";

class PartnerDashboardService
  extends BaseService
  implements IPartnerDashboardService
{
  private getBasePath = (tenantId: string) =>
    `/tenants/${tenantId}/partners/dashboard`;

  constructor() {
    super();
  }

  getHotels(
    tenantId: string,
    query: PartnerHotelOverviewQuery,
  ): Promise<PartnerHotelOverview[]> {
    return this.get<PartnerHotelOverview[]>(
      `${this.getBasePath(tenantId)}/hotels`,
      query,
    );
  }

  getHotelFilterOptions(tenantId: string): Promise<PartnerHotelFilterOption[]> {
    return this.get<PartnerHotelFilterOption[]>(
      `${this.getBasePath(tenantId)}/hotels/filter-options`,
    );
  }

  getTopRevenueHotel(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerHotelRevenueRank | null> {
    return this.get<PartnerHotelRevenueRank | null>(
      `${this.getBasePath(tenantId)}/top-revenue-hotel`,
      query,
    );
  }

  getLowestRevenueHotel(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerHotelRevenueRank | null> {
    return this.get<PartnerHotelRevenueRank | null>(
      `${this.getBasePath(tenantId)}/lowest-revenue-hotel`,
      query,
    );
  }

  getTotalRevenue(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerTotalRevenueSummary> {
    return this.get<PartnerTotalRevenueSummary>(
      `${this.getBasePath(tenantId)}/total-revenue`,
      query,
    );
  }

  async exportHotels(
    tenantId: string,
    query: PartnerHotelExportQuery,
  ): Promise<Blob> {
    const queryString = new URLSearchParams();
    queryString.append("from", query.from);
    queryString.append("to", query.to);
    queryString.append("format", query.format);
    query.hotelIds?.forEach((hotelId) =>
      queryString.append("hotelIds", hotelId),
    );

    const response = await fetch(
      `${this.baseUrl}${this.getBasePath(tenantId)}/hotels/export?${queryString.toString()}`,
      {
        method: "GET",
        headers: {
          ...(this.getAuthorizationHeader() ?? {}),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to export hotels: ${response.status}`);
    }

    return response.blob();
  }

  getRevenueLogs(
    tenantId: string,
    query: PartnerRevenueLogsQuery,
  ): Promise<PartnerRevenueLogsResponse> {
    return this.get<PartnerRevenueLogsResponse>(
      `${this.getBasePath(tenantId)}/revenue-logs`,
      query,
    );
  }

  async exportRevenueLogs(
    tenantId: string,
    query: PartnerRevenueLogsExportQuery,
  ): Promise<Blob> {
    const queryString = new URLSearchParams();
    queryString.append("from", query.from);
    queryString.append("to", query.to);
    queryString.append("format", query.format);
    if (query.hotelId) {
      queryString.append("hotelId", query.hotelId);
    }

    const response = await fetch(
      `${this.baseUrl}${this.getBasePath(tenantId)}/revenue-logs/export?${queryString.toString()}`,
      {
        method: "GET",
        headers: {
          ...(this.getAuthorizationHeader() ?? {}),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to export revenue logs: ${response.status}`);
    }

    return response.blob();
  }
}

export const partnerDashboardService = new PartnerDashboardService();
